/**
 * PlanEditForm.tsx — Side-panel form for creating and editing billing plans.
 *
 * Renders as panel content (no modal wrapper). Manages plan basics
 * (name, interval, features) and pricing models dynamically — add, remove,
 * and configure each model type inline.
 *
 * @module packages/billing-admin/src/components/plan/PlanEditForm
 * @since 2026-06-01
 */
import React, { useState, useEffect } from 'react';
import type { Plan } from '../../hooks/useAdminApi50';
import { usePlanPricingModels, useAddPricingModel, useUpdatePricingModel, useDeletePricingModel } from '../../hooks/useAdminApi50';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import PricingModelConfigForm, { MODEL_TYPE_LABELS, MODEL_TYPE_COLORS, formatModelSummary } from './PricingModelConfigForm';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PlanEditFormProps {
  plan: Plan | null;
  onSuccess: () => void;
  onCancel: () => void;
}

type LocalModel = {
  id: string | null; // null = new (not yet saved)
  model_type: 'flat' | 'per_seat' | 'usage' | 'tiered';
  config: Record<string, unknown>;
  is_active: boolean;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function PlanEditForm({ plan, onSuccess, onCancel }: PlanEditFormProps): React.JSX.Element {
  const { token, apiBaseUrl } = useAdminAuth();
  const isEdit = plan !== null;

  // Plan basics
  const [name, setName] = useState(plan?.name ?? '');
  const [description, setDescription] = useState((plan as Record<string, unknown> | null)?.description as string ?? '');
  const [interval, setInterval] = useState<'monthly' | 'annual'>(plan?.interval ?? 'monthly');
  const [features, setFeatures] = useState(plan?.features?.join(', ') ?? '');

  // Pricing models — loaded from API for existing plans
  const { data: serverModels } = usePlanPricingModels(isEdit ? plan.id : null);
  const addModelMut = useAddPricingModel();
  const updateModelMut = useUpdatePricingModel();
  const deleteModelMut = useDeletePricingModel();

  const [localModels, setLocalModels] = useState<LocalModel[]>([]);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [addingType, setAddingType] = useState<string | null>(null);

  // Sync server models into local state when they arrive
  useEffect(() => {
    if (serverModels && serverModels.length > 0 && localModels.length === 0) {
      setLocalModels(serverModels.map((m) => ({
        id: m.id,
        model_type: m.model_type as LocalModel['model_type'],
        config: m.config as Record<string, unknown>,
        is_active: m.is_active,
      })));
    }
  }, [serverModels]);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Model management
  // ---------------------------------------------------------------------------

  function addModel(type: string): void {
    const defaults: Record<string, Record<string, unknown>> = {
      flat: { amount_cents: 0, currency: 'USD' },
      per_seat: { per_seat_cents: 0, currency: 'USD', included_seats: 0 },
      usage: { billing_mode: 'credits' },
      tiered: { tiers: [], currency: 'USD' },
    };
    setLocalModels((prev) => [...prev, {
      id: null,
      model_type: type as LocalModel['model_type'],
      config: defaults[type] ?? {},
      is_active: true,
    }]);
    setExpandedIdx(localModels.length); // expand the new one
    setAddingType(null);
  }

  function removeModel(idx: number): void {
    setLocalModels((prev) => prev.filter((_, i) => i !== idx));
    if (expandedIdx === idx) setExpandedIdx(null);
    else if (expandedIdx !== null && expandedIdx > idx) setExpandedIdx(expandedIdx - 1);
  }

  function updateModelConfig(idx: number, config: Record<string, unknown>): void {
    setLocalModels((prev) => prev.map((m, i) => i === idx ? { ...m, config } : m));
  }

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------

  async function handleSave(): Promise<void> {
    if (!token) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      const planId = plan?.id;

      // 1. Save plan basics
      const payload = {
        name,
        interval,
        pricing_model: localModels[0]?.model_type ?? plan?.pricing_model ?? 'flat',
        price: 0, // legacy field — pricing now lives in models
        currency: 'USD',
        features: features ? features.split(',').map((f) => f.trim()).filter(Boolean) : [],
      };

      const url = isEdit ? `${apiBaseUrl}/admin/plans/${planId}` : `${apiBaseUrl}/admin/plans`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: res.statusText }));
        throw new Error((err as { message?: string }).message ?? res.statusText);
      }

      const savedPlan = isEdit ? plan : (await res.json()).data;
      const savedPlanId = savedPlan?.id ?? planId;

      if (!savedPlanId) throw new Error('No plan ID returned');

      // 2. Sync pricing models — delete removed, update existing, create new
      const existingIds = new Set(localModels.filter((m) => m.id).map((m) => m.id));
      const serverIds = (serverModels ?? []).map((m) => m.id);

      // Delete models that were removed
      for (const id of serverIds) {
        if (!existingIds.has(id)) {
          await deleteModelMut.mutateAsync({ planId: savedPlanId, modelId: id });
        }
      }

      // Create or update each model
      for (let i = 0; i < localModels.length; i++) {
        const lm = localModels[i];
        if (lm.id) {
          // Update existing
          await updateModelMut.mutateAsync({
            planId: savedPlanId,
            modelId: lm.id,
            updates: { config: lm.config, display_order: i },
          });
        } else {
          // Create new
          await addModelMut.mutateAsync({
            planId: savedPlanId,
            model_type: lm.model_type,
            config: lm.config,
            display_order: i,
          });
        }
      }

      onSuccess();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Plan Basics */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700">Plan Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            placeholder="e.g. Business, Enterprise"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            rows={2}
            placeholder="Plan description (synced to Stripe product)"
          />
        </div>

        {(plan as unknown as Record<string, unknown> | null)?.stripe_product_id ? (
          <div className="rounded-md bg-cyan-50 px-3 py-2 text-xs text-cyan-700">
            ✓ Synced to Stripe: <code className="font-mono">{String((plan as unknown as Record<string, unknown>).stripe_product_id)}</code>
          </div>
        ) : null}

        <div>
          <label className="block text-sm font-medium text-slate-700">Billing Interval</label>
          <select
            value={interval}
            onChange={(e) => setInterval(e.target.value as 'monthly' | 'annual')}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700">
            Features <span className="text-slate-400 font-normal">(comma-separated)</span>
          </label>
          <input
            type="text"
            value={features}
            onChange={(e) => setFeatures(e.target.value)}
            placeholder="Cloud Sync, Agent Studio, Policy Studio"
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          <p className="mt-1 text-xs text-slate-500">Features map to rule42-auth business roles for automatic provisioning.</p>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-slate-200" />

      {/* Pricing Models */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">Pricing Models</p>
            <p className="text-xs text-slate-500 mt-0.5">Stack multiple pricing models. Each applies independently.</p>
          </div>
        </div>

        {localModels.length === 0 && (
          <div className="rounded-xl border-2 border-dashed border-slate-200 p-6 text-center mb-3">
            <p className="text-sm text-slate-500">No pricing models yet</p>
            <p className="text-xs text-slate-400 mt-1">Add at least one model to define how this plan charges customers.</p>
          </div>
        )}

        <div className="space-y-2 mb-3">
          {localModels.map((model, idx) => {
            const isExpanded = expandedIdx === idx;
            return (
              <div key={`${model.model_type}-${idx}`} className={`rounded-xl border transition ${isExpanded ? 'border-cyan-300 shadow-sm' : MODEL_TYPE_COLORS[model.model_type] ?? 'border-slate-200 bg-slate-50'}`}>
                {/* Collapsed header */}
                <div
                  onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                  className="flex items-center gap-3 p-3 cursor-pointer"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-slate-900">{MODEL_TYPE_LABELS[model.model_type] ?? model.model_type}</p>
                      {!isExpanded && (
                        <span className="text-xs text-slate-500">{formatModelSummary(model.model_type, model.config)}</span>
                      )}
                    </div>
                  </div>
                  <svg className={`h-4 w-4 text-slate-400 transition ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                  </svg>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeModel(idx); }}
                    className="rounded-md px-2 py-1 text-[10px] font-medium text-red-600 hover:bg-red-50"
                  >✕</button>
                </div>

                {/* Expanded config */}
                {isExpanded && (
                  <div className="border-t border-slate-200 px-4 py-4 bg-white rounded-b-xl">
                    <PricingModelConfigForm
                      modelType={model.model_type}
                      config={model.config}
                      setConfig={(c) => updateModelConfig(idx, c)}
                      compact
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Add model */}
        {addingType === null ? (
          <button
            type="button"
            onClick={() => setAddingType('pick')}
            className="w-full rounded-xl border-2 border-dashed border-slate-300 py-3 text-sm font-medium text-cyan-700 hover:border-cyan-400 hover:bg-cyan-50/50 transition"
          >
            + Add Pricing Model
          </button>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-medium text-slate-700 mb-3">Select model type to add</p>
            <div className="grid grid-cols-2 gap-2">
              {(['flat', 'per_seat', 'usage', 'tiered'] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => addModel(type)}
                  className={`rounded-lg border p-3 text-left transition hover:shadow-sm ${MODEL_TYPE_COLORS[type]}`}
                >
                  <p className="text-sm font-semibold">{MODEL_TYPE_LABELS[type]}</p>
                  <p className="text-[11px] mt-0.5 opacity-70">
                    {type === 'flat' && 'Fixed monthly fee'}
                    {type === 'per_seat' && 'Per user charge'}
                    {type === 'usage' && 'Metered billing'}
                    {type === 'tiered' && 'Volume brackets'}
                  </p>
                </button>
              ))}
            </div>
            <button onClick={() => setAddingType(null)} className="mt-2 text-xs text-slate-500 hover:text-slate-700">Cancel</button>
          </div>
        )}
      </div>

      {/* Error */}
      {submitError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{submitError}</div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="flex-1 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={submitting || !name.trim()}
          className="flex-1 rounded-xl bg-cyan-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-cyan-800 disabled:opacity-50 transition"
        >
          {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Plan'}
        </button>
      </div>
    </div>
  );
}
// EOF
